import jsPDF from 'jspdf';

interface SalaryData {
  id: string;
  jobTitle: string;
  country: string;
  city: string;
  sector: string;
  subsector?: string;
  experienceLevel: string;
  yearsOfExperience: number;
  averageSalary: number;
  minSalary: number;
  maxSalary: number;
  medianSalary: number;
  currency: string;
  dataSource: string;
  lastUpdated: string;
  sampleSize?: number;
}

interface ComparisonItem extends SalaryData {
  difference?: number;
  percentageDifference?: number;
}

interface SearchCriteria {
  jobTitle: string;
  country: string;
  city: string;
  sector: string;
  subsector: string;
  experienceLevel: string;
  yearsOfExperience: number;
}

export const generateSimpleSalaryPDF = (
  searchCriteria: SearchCriteria,
  searchResults: SalaryData | null,
  comparisons: ComparisonItem[],
  currency: string,
  t: (key: string) => string
) => {
  try {
    console.log('Starting PDF generation...');
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPosition = 20;

    console.log('PDF document created, adding content...');

    // Colors matching Finanzly brand
    const primaryColor = [234, 88, 12] as const; // #EA580C
    const textColor = [64, 64, 64] as const; // #404040

    // Currency formatting
    const formatCurrency = (amount: number) => {
      const formattedAmount = amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      
      if (currency === "USD") {
        return `$${formattedAmount}`;
      } else if (currency === "GBP") {
        return `£${formattedAmount}`;
      } else if (currency === "CHF") {
        return `CHF ${formattedAmount}`;
      } else {
        // EUR - symbol goes to the right
        return `${formattedAmount} €`;
      }
    };

    console.log('Adding header...');

    // Header with Finanzly branding
    doc.setFontSize(24);
    doc.setTextColor(...primaryColor);
    doc.text('FINANZLY', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    doc.setFontSize(18);
    doc.setTextColor(...textColor);
    doc.text(t('tools.salaryComparison.title') || 'Salary Comparison Tool', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    console.log('Adding date and URL...');

    // Date and URL
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    doc.text('https://finanzly.es/tools/salary-comparison', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    console.log('Adding search summary...');

    // Search Summary Section
    doc.setFontSize(16);
    doc.setTextColor(...primaryColor);
    doc.text(t('tools.salaryComparison.searchResults') || 'Search Results', 20, yPosition);
    yPosition += 10;

    // Search criteria - simple text version
    doc.setFontSize(12);
    doc.setTextColor(...textColor);
    
    const searchInfo = [
      `Country: ${searchCriteria.country}`,
      `City: ${searchCriteria.city}`,
      `Sector: ${searchCriteria.sector}`,
      `Experience: ${searchCriteria.experienceLevel}`,
    ];

    searchInfo.forEach((info, index) => {
      doc.text(info, 20, yPosition + (index * 6));
    });
    
    yPosition += 40;

    console.log('Adding main results...');

    // Main Search Results
    if (searchResults) {
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text(t('tools.salaryComparison.salaryRange') || 'Salary Range', 20, yPosition);
      yPosition += 10;

      // Simple text version of salary data
      const salaryInfo = [
        `Average Salary: ${formatCurrency(searchResults.averageSalary)}`,
        `Min Salary: ${formatCurrency(searchResults.minSalary)}`,
        `Max Salary: ${formatCurrency(searchResults.maxSalary)}`,
        `Median Salary: ${formatCurrency(searchResults.medianSalary)}`,
      ];

      doc.setFontSize(12);
      doc.setTextColor(...textColor);
      salaryInfo.forEach((info, index) => {
        doc.text(info, 20, yPosition + (index * 6));
      });

      yPosition += 30;
    }

    console.log('Adding comparisons...');

    // Comparisons Section - simple text version
    if (comparisons.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text(t('tools.salaryComparison.comparisonResults') || 'Comparison Results', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setTextColor(...textColor);

      comparisons.forEach((comparison, index) => {
        const comparisonText = `${comparison.country}, ${comparison.city}: ${formatCurrency(comparison.averageSalary)}`;
        doc.text(comparisonText, 20, yPosition + (index * 6));
      });

      yPosition += comparisons.length * 6 + 20;
    }

    console.log('Adding footer...');

    // Simple footer
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('Finanzly.es - Herramientas Financieras', pageWidth / 2, 280, { align: 'center' });

    console.log('Saving PDF...');

    // Save the PDF
    const fileName = `salary-comparison-simple-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    console.log('PDF saved successfully!');
    return true;
  } catch (error) {
    console.error('Error in simple PDF generation:', error);
    console.error('Error details:', {
      message: (error as Error).message,
      stack: (error as Error).stack
    });
    throw error;
  }
};